import { ICareerSkillNode } from 'core/hooks/CareerSkillGraph/transform/transformCareerSkillGraph';

/**
 * Как назвать уровень навыка словами (ТЗ 0071, п. 6.3).
 *
 * Проверка в Brave: «5/3» на узле карты не объясняло, где чей уровень. Теперь
 * подпись выбирается по положению относительно ТРЕБОВАНИЯ, которое считает
 * сервер (`requirementStatus`), и одна функция кормит холст, список и
 * доступное имя — три места, которые иначе разошлись бы в формулировках.
 *
 *   соответствует   → «Spełnia wymagania»         (уровень не нужен)
 *   ниже требуемого → «Poziom 2 → wymagany 3»
 *   откроется       → «W trakcie · poziom 3»      (навык придёт с курсом)
 *   нет             → «Do zdobycia · poziom 3»
 *
 * Полная фраза «Twój poziom: 5 · wymagany: 3» — в паспорте, в списке и в
 * доступном имени карточки.
 */
export interface ILevelText {
  key: string;
  vars: Record<string, number>;
}

type LevelNode = Pick<ICareerSkillNode, 'requirementStatus' | 'myLevel' | 'requiredLevel'> & {
  upcoming?: ICareerSkillNode['upcoming'];
};

/**
 * Положение навыка словами ТЗ 0071: «Spełnia wymagania», «Do rozwinięcia»,
 * «W trakcie», «Do zdobycia».
 *
 * 🔴 Одна функция на холст, легенду и паспорт. Порядок проверок — тот же, что
 * у строки статуса под именем: иначе узел, на котором написано «W trakcie ·
 * poziom 3», в паспорте назывался бы «Do zdobycia», и человек решил бы, что
 * смотрит два разных навыка.
 */
export type RequirementDisplay = 'meets' | 'improve' | 'upcoming' | 'missing';

export const requirementDisplay = (node: LevelNode): RequirementDisplay => {
  if (node.requirementStatus === 'meets') return 'meets';
  if (node.requirementStatus === 'improve' && node.myLevel !== null) return 'improve';
  if (node.upcoming) return 'upcoming';
  return 'missing';
};

/**
 * Подписи положений — СЛОВАРЁМ литералов, а не шаблоном ключа: сканер паритета
 * локалей видит только литералы, и динамический ключ прошёл бы мимо него.
 */
export const REQUIREMENT_LABEL_KEY: Record<RequirementDisplay, string> = {
  meets: 'careerCenter.map.requirement.meets',
  improve: 'careerCenter.map.requirement.improve',
  upcoming: 'careerCenter.map.requirement.upcoming',
  missing: 'careerCenter.map.requirement.missing',
};

/** Короткая строка под именем на карточке холста. */
export const levelLine = (node: LevelNode): ILevelText => {
  const required = node.requiredLevel;
  const display = requirementDisplay(node);
  if (display === 'meets') {
    return { key: 'careerCenter.map.levelLine.meets', vars: {} };
  }
  if (display === 'improve') {
    return {
      key: 'careerCenter.map.levelLine.improve',
      vars: { mine: node.myLevel as number, required },
    };
  }
  if (display === 'upcoming') {
    return { key: 'careerCenter.map.levelLine.upcoming', vars: { required } };
  }
  return { key: 'careerCenter.map.levelLine.missing', vars: { required } };
};

/**
 * Полная фраза уровня. У отсутствующего навыка «твой уровень 0» не пишется:
 * уровня нет, а не нулевой.
 */
export const levelPhrase = (node: LevelNode): ILevelText =>
  node.requirementStatus === 'missing' || node.myLevel === null
    ? { key: 'careerCenter.map.levelsNone', vars: { required: node.requiredLevel } }
    : {
        key: 'careerCenter.map.levels',
        vars: { mine: node.myLevel, required: node.requiredLevel },
      };
